import { useEffect, useRef, type ReactNode } from 'react';
import type { QuestionAnswerInput } from '../api/client';
import type { ProposalStatus, QuestionAnswer, StudioMessage, StudioQuestion } from '../api/types';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { proposalStatusView } from '../app/status';
import { InlineMarkdown, Markdown } from '../lib/markdown';
import { QuestionCard } from './QuestionCard';
import { WorkLog } from './WorkLog';

export interface MessageListProps {
  /** seq の昇順。 */
  messages: StudioMessage[];
  /** 質問文と状態を引くための、知っている質問。 */
  questions: StudioQuestion[];
  onAnswer(questionId: string, answers: QuestionAnswerInput[]): Promise<void>;
  onResume(): void;
  /** 「別の指示を書く」で入力欄へ移る。 */
  onFocusComposer(): void;
  /** AI の変更がターンの終わりに自動で反映される appId（公開前の Draft。U8）。行は app ごとなので集合で受ける。 */
  autoApplyApps?: ReadonlySet<string>;
  /** 反映済みの行から版の履歴（microapp のコードタブ）へ移る。渡さなければリンクを出さない。 */
  onOpenHistory?(appId: string): void;
}

type Block = { kind: 'message'; message: StudioMessage } | { kind: 'worklog'; entries: StudioMessage[] };

const MEMORY_ACTIONS: Record<string, string> = { created: '記憶に追加', updated: '記憶を更新', retired: '記憶を退役' };
const PROPOSAL_STATUSES: readonly string[] = ['pending', 'interrupted', 'accepted', 'discarded'];
/** 公開前の Draft に Draft ごとに 1 度だけ出す注記（U8）。 */
export const APPLIED_NOTE = '公開前は AI の変更を自動で反映します。差分は版の履歴で見られます';
const SHOWN_STATUSES: readonly string[] = ['cancelled', 'failed', 'resumed'];
/** 最下部からこの距離（px）以内なら「追従中」とみなす。 */
const AUTO_SCROLL_THRESHOLD_PX = 48;

/** 質問文と選択肢の label。API は open の質問しか返さないので、行の body に写したものを使う。 */
interface QuestionOutline {
  n: number;
  kind: string;
  text: string;
  options: { id: string; label: string }[];
}

interface RenderContext {
  questions: Map<string, StudioQuestion>;
  /** questionId → 行の body に写した質問文（開き直した後の会話の記録）。 */
  outlines: Map<string, QuestionOutline[]>;
  isLastStatus: boolean;
  /** 自動反映が働く appId（U8）。 */
  autoApplyApps: ReadonlySet<string>;
  /** 注記を添える行の id（Draft ごとに 1 つ）。 */
  noteIds: Set<string>;
  onAnswer(questionId: string, answers: QuestionAnswerInput[]): Promise<void>;
  onResume(): void;
  onFocusComposer(): void;
  onOpenHistory: ((appId: string) => void) | undefined;
}

/** 表示用メッセージ（6.8）を kind ごとに描く。連続する tool_call は作業ログ 1 つにまとめる。 */
export function MessageList({
  messages,
  questions,
  onAnswer,
  onResume,
  onFocusComposer,
  autoApplyApps,
  onOpenHistory,
}: MessageListProps) {
  const listRef = useRef<HTMLOListElement>(null);
  const lastMessageId = messages.at(-1)?.id ?? null;
  // 最新の行が変わるたびに最下部へ追従する。利用者が過去の行を見に上へスクロールしている間は追従しない。
  useEffect(() => {
    const node = listRef.current;
    if (node === null) return;
    const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
    if (distanceFromBottom > AUTO_SCROLL_THRESHOLD_PX) return;
    node.scrollTop = node.scrollHeight;
  }, [lastMessageId]);
  const byId = new Map(questions.map((question) => [question.id, question]));
  const outlines = new Map(
    messages
      .filter((message) => message.kind === 'question')
      .map((message) => [text(message.body['questionId']), outlineOf(message.body['questions'])] as const),
  );
  const lastStatusId = [...messages].reverse().find((message) => message.kind === 'status')?.id ?? null;
  // 公開前の注記は Draft ごとに 1 度だけ（自動で反映された最初の行に添える。U8）。
  const applyApps: ReadonlySet<string> = autoApplyApps ?? new Set<string>();
  const noteIds = new Set<string>();
  const seen = new Set<string>();
  for (const message of messages) {
    if (message.kind !== 'proposal' || text(message.body['status']) !== 'accepted') continue;
    const appId = text(message.body['appId']);
    if (!applyApps.has(appId) || seen.has(appId)) continue;
    seen.add(appId);
    noteIds.add(message.id);
  }
  const rows: Array<{ key: string; className: string; content: ReactNode }> = [];
  for (const block of groupBlocks(messages)) {
    if (block.kind === 'worklog') {
      rows.push({
        key: `log:${block.entries[0]?.id ?? ''}`,
        className: 'studio-message studio-message-log',
        content: <WorkLog entries={block.entries} />,
      });
      continue;
    }
    const content = renderMessage(block.message, {
      questions: byId,
      outlines,
      isLastStatus: block.message.id === lastStatusId,
      autoApplyApps: applyApps,
      noteIds,
      onAnswer,
      onResume,
      onFocusComposer,
      onOpenHistory,
    });
    if (content !== null) {
      rows.push({ key: block.message.id, className: `studio-message studio-message-${block.message.role}`, content });
    }
  }
  return (
    <ol className="studio-messages" aria-label="会話" ref={listRef}>
      {rows.map((row) => (
        <li key={row.key} className={row.className}>
          {row.content}
        </li>
      ))}
    </ol>
  );
}

function groupBlocks(messages: readonly StudioMessage[]): Block[] {
  const blocks: Block[] = [];
  for (const message of messages) {
    const last = blocks.at(-1);
    if (message.kind !== 'tool_call') blocks.push({ kind: 'message', message });
    else if (last?.kind === 'worklog') last.entries.push(message);
    else blocks.push({ kind: 'worklog', entries: [message] });
  }
  return blocks;
}

function renderMessage(message: StudioMessage, context: RenderContext): ReactNode {
  const body = message.body;
  switch (message.kind) {
    case 'text': {
      const attachments = Array.isArray(body['attachments']) ? (body['attachments'] as unknown[]) : [];
      const names = attachments.map((attachment) =>
        attachment !== null && typeof attachment === 'object' ? text((attachment as Record<string, unknown>)['name']) : '',
      );
      return (
        <>
          {/* AI の本文は Markdown で描く（U3）。利用者の本文は書いたままを出す。 */}
          {message.role === 'assistant' ? (
            <Markdown className="studio-text" text={text(body['text'])} />
          ) : (
            <p className="studio-text">{text(body['text'])}</p>
          )}
          {names.length > 0 && <p className="studio-attachments">{`添付: ${names.join('、')}`}</p>}
        </>
      );
    }
    case 'question': {
      const questionId = text(body['questionId']);
      const question = context.questions.get(questionId);
      if (question === undefined) {
        // ページを開き直した後は、閉じた質問を読む経路が無い。行に写した質問文だけを出す。
        const outline = context.outlines.get(questionId) ?? [];
        return outline.length === 0 ? null : <PastQuestion items={outline} />;
      }
      if (question.status === 'open') {
        return <QuestionCard question={question} onSubmit={(answers) => context.onAnswer(question.id, answers)} />;
      }
      if (question.status === 'superseded') return <p className="hint">質問は別の指示で取り下げられました</p>;
      return null;
    }
    case 'answer': {
      const questionId = text(body['questionId']);
      const known = context.questions.get(questionId);
      const items = known ? outlineOf(known.body.questions) : (context.outlines.get(questionId) ?? []);
      return <AnswerLines items={items} answers={answersOf(body['answers'])} />;
    }
    case 'memory':
      return (
        <p className="studio-memory-line">
          {`${MEMORY_ACTIONS[text(body['action'])] ?? '記憶に追加'} · [${text(body['name'])}] `}
          <InlineMarkdown text={text(body['text'])} />
        </p>
      );
    case 'user_change':
      return (
        <>
          <p className="studio-user-change-title">利用者の変更を AI に伝えました</p>
          {text(body['text']) && <pre className="studio-user-change">{text(body['text'])}</pre>}
        </>
      );
    case 'proposal': {
      const status = text(body['status']);
      // 公開前は AI の変更をターンの終わりに自動で反映する。差分は版の履歴で見てもらう（U8）。
      if (status === 'accepted' && context.autoApplyApps.has(text(body['appId']))) {
        return (
          <AppliedProposal body={body} note={context.noteIds.has(message.id)} onOpenHistory={context.onOpenHistory} />
        );
      }
      const label = PROPOSAL_STATUSES.includes(status) ? proposalStatusView(status as ProposalStatus).label : status;
      return <p className="studio-proposal-line">{`変更案 · ${label}`}</p>;
    }
    case 'status': {
      if (!SHOWN_STATUSES.includes(text(body['status']))) return null;
      return (
        <StatusLine body={body} isLast={context.isLastStatus} onResume={context.onResume} onFocusComposer={context.onFocusComposer} />
      );
    }
    case 'tool_call':
      return null;
  }
}

/** 公開前に自動で反映された変更案の 1 行（U8）。差分は版の履歴で見る。 */
function AppliedProposal({
  body,
  note,
  onOpenHistory,
}: {
  body: Record<string, unknown>;
  note: boolean;
  onOpenHistory: ((appId: string) => void) | undefined;
}) {
  const appId = text(body['appId']);
  return (
    <>
      <p className="studio-proposal-line">
        <span>{`変更を反映しました（${fileCountOf(body['summary'])} ファイル）`}</span>
        {onOpenHistory !== undefined && appId !== '' && (
          <Button variant="text" onClick={() => onOpenHistory(appId)}>
            版の履歴で見る
          </Button>
        )}
      </p>
      {note && <p className="hint">{APPLIED_NOTE}</p>}
    </>
  );
}

function PastQuestion({ items }: { items: QuestionOutline[] }) {
  return (
    <div className="studio-question-past">
      {items.map((item) => (
        <p key={item.n} className="studio-question-past-line">
          <span>{`Q${item.n} ${item.text}`}</span>
          {item.options.length > 0 && <span className="hint">{`選択肢: ${item.options.map((option) => option.label).join('、')}`}</span>}
        </p>
      ))}
    </div>
  );
}

function AnswerLines({ items, answers }: { items: QuestionOutline[]; answers: QuestionAnswer[] }) {
  return (
    <div className="studio-answer">
      {answers.map((answer) => {
        const item = items.find((candidate) => candidate.n === answer.n);
        const labels = answer.choices.map((id) => item?.options.find((option) => option.id === id)?.label ?? id);
        const value = answer.defer
          ? '未確定のまま進める'
          : item?.kind === 'text' || labels.length === 0
            ? (answer.text ?? '')
            : `${labels.join('、')}${answer.text ? ` / 補足: ${answer.text}` : ''}`;
        const head = item ? `Q${answer.n} ${item.text}` : `Q${answer.n}`;
        return (
          <p key={answer.n} className="studio-answer-line">
            <span>{`${head} → ${value}`}</span>
            <Badge tone={answer.defer ? 'unknown' : 'completed'}>{answer.defer ? 'あとで決める' : '回答済み'}</Badge>
          </p>
        );
      })}
    </div>
  );
}

function StatusLine({
  body,
  isLast,
  onResume,
  onFocusComposer,
}: {
  body: Record<string, unknown>;
  isLast: boolean;
  onResume(): void;
  onFocusComposer(): void;
}) {
  const status = text(body['status']);
  if (status === 'resumed') return <p className="hint">続きから再開しました</p>;
  if (status === 'cancelled') {
    return (
      <div className="studio-status" role="status">
        <span>中断しました</span>
        <span className="hint">途中の変更は変更案として残っています。</span>
        {isLast && (
          <div className="row">
            <Button onClick={onResume}>続きから再開</Button>
            <Button variant="text" onClick={onFocusComposer}>
              別の指示を書く
            </Button>
          </div>
        )}
      </div>
    );
  }
  const error = body['error'];
  const code = error !== null && typeof error === 'object' ? text((error as Record<string, unknown>)['code']) : '';
  return (
    <div className="studio-status studio-status-failed" role="alert">
      <span>{`AI の作業が失敗しました（${code || 'unknown'}）`}</span>
      {isLast && (
        <div className="row">
          <Button onClick={onResume}>続きから再開</Button>
        </div>
      )}
    </div>
  );
}

/** 行の body に写した questions（と StudioQuestion の body.questions）を、描くのに要る形だけにする。 */
function outlineOf(raw: unknown): QuestionOutline[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry): QuestionOutline[] => {
    if (entry === null || typeof entry !== 'object') return [];
    const record = entry as Record<string, unknown>;
    if (typeof record['n'] !== 'number') return [];
    const options = Array.isArray(record['options']) ? record['options'] : [];
    return [
      {
        n: record['n'],
        kind: text(record['kind']),
        text: text(record['text']),
        options: options.flatMap((option): { id: string; label: string }[] => {
          if (option === null || typeof option !== 'object') return [];
          const values = option as Record<string, unknown>;
          return [{ id: text(values['id']), label: text(values['label']) }];
        }),
      },
    ];
  });
}

function answersOf(raw: unknown): QuestionAnswer[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry): QuestionAnswer[] => {
    if (entry === null || typeof entry !== 'object') return [];
    const record = entry as Record<string, unknown>;
    if (typeof record['n'] !== 'number') return [];
    return [
      {
        n: record['n'],
        choices: Array.isArray(record['choices']) ? record['choices'].filter((choice): choice is string => typeof choice === 'string') : [],
        text: typeof record['text'] === 'string' ? record['text'] : null,
        defer: record['defer'] === true,
      },
    ];
  });
}

/** 変更案の要約のファイル数（ターンの途中は summary が null）。 */
function fileCountOf(raw: unknown): number {
  if (raw === null || typeof raw !== 'object') return 0;
  const files = (raw as Record<string, unknown>)['files'];
  return typeof files === 'number' ? files : 0;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}
